import React, { useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'
import type { ReadonlyDeep } from 'type-fest'

import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import type {
  AnyStepContentElement,
  InjectedStepContentRendererProps,
} from '@flows/core/types/stepContentTypes'
import { body2, h4 } from '@ui-kit/core/typography'

const underInsuredWarningStyles = tv({
  slots: {
    wrapper: 'gap-y-3',
    infoCard: 'rounded-lg border border-warning p-4',
    infoTitle: h4(),
    infoSubtitle: body2({ color: 'grey700' }),
    resetButton: 'items-center rounded-lg border border-grey-300 py-3',
    resetLabel: body2(),
    modal: 'absolute inset-0 z-50 bg-white p-5',
    closeButton: 'self-end pb-4',
  },
})

type Props = InjectedStepContentRendererProps<AnyStepContentElement> & {
  insuredSumPath: string
  getRecommendedInsuredSum: (flowValues: Record<string, unknown>) => number
  warningModalConfiguration?: ReadonlyDeep<NonNullable<AnyStepContentElement>[]>
}

export const UnderInsuredWarning = (props: Props) => {
  const { t } = useTranslation()
  const { flowValues, updateFlowValues, flowName } = useQuoteData()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const styles = underInsuredWarningStyles()

  const resetInsuredSumAmount = () => {
    updateFlowValues({
      [props.insuredSumPath]: props.getRecommendedInsuredSum(flowValues),
    })
  }

  return (
    <>
      <View testID="UnderInsuredWarning" className={styles.wrapper()}>
        <Pressable onPress={() => setIsModalOpen(true)} className={styles.infoCard()}>
          <Text className={styles.infoTitle()}>
            {t('sharedFlows_common:customComponents.underinsuredWarning.infoCTA.title')}
          </Text>
          <Text className={styles.infoSubtitle()}>
            {t('sharedFlows_common:customComponents.underinsuredWarning.infoCTA.subtitle')}
          </Text>
        </Pressable>
        <Pressable onPress={resetInsuredSumAmount} className={styles.resetButton()}>
          <Text className={styles.resetLabel()}>
            {t('sharedFlows_common:customComponents.underinsuredWarning.resetCTA.label')}
          </Text>
        </Pressable>
      </View>
      {isModalOpen && props.warningModalConfiguration && (
        <View className={styles.modal()}>
          <Pressable onPress={() => setIsModalOpen(false)} className={styles.closeButton()}>
            <Text>X</Text>
          </Pressable>
          {props.stepContentRenderer({
            elements: props.warningModalConfiguration,
            componentMap: props.componentMap,
            flowValues,
            flowName,
          })}
        </View>
      )}
    </>
  )
}
